import { useMemo, useState } from "react";

import { Button, makeStyles } from "@material-ui/core";

import FormTextField from "src/components/form/FormTextField";
import FormVanSearchField, {
  VanSearchFieldValue
} from "src/components/form/FormVanSearchField";
import SubmitButton from "src/components/form/SubmitButton";
import { getCampaign_campaign } from "src/graphql/generated/getCampaign";
import { VanProxySearchType } from "src/graphql/generated/globalTypes";
import { useAutocompleteStyles, useFormInputStyles } from "src/styles/forms";

type PickerMode = "SEARCH" | "LIST_ID";

const LIST_ID_PATTERN = /^\d{1,9}$/;

const useStyles = makeStyles(theme => ({
  pickerContainer: {
    marginBottom: theme.spacing(2)
  },
  listIdInputRoot: {
    marginTop: 10 // Matches the marginTop used by FormVanSearchField's Autocomplete
  },
  hiddenNotchedOutline: {
    display: "none"
  }
}));

export default function VanSavedListPicker({
  campaign,
  onSubmit
}: {
  campaign: getCampaign_campaign;
  onSubmit: (listId: number) => any;
}): JSX.Element {
  const classes = useStyles();
  const formInputStyles = useFormInputStyles();
  const autocompleteStyles = useAutocompleteStyles();

  const hideVanListSearch =
    campaign.vanIntegration?.vanIntegration.hideVanListSearch ?? false;

  const [mode, setMode] = useState<PickerMode>(
    hideVanListSearch ? "LIST_ID" : "SEARCH"
  );
  const [selectedList, setSelectedList] = useState<VanSearchFieldValue | null>(
    null
  );
  const [enteredListId, setEnteredListId] = useState("");
  const [enteredListIdTouched, setEnteredListIdTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const listId: number | null = useMemo(() => {
    if (mode === "SEARCH") {
      return selectedList?.id ?? null;
    } else {
      const trimmedEnteredListId = enteredListId.trim();
      if (LIST_ID_PATTERN.test(trimmedEnteredListId)) {
        return parseInt(trimmedEnteredListId);
      } else {
        return null;
      }
    }
  }, [mode, selectedList, enteredListId]);

  const handleSubmit = async () => {
    if (!listId) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(listId);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className={classes.pickerContainer}>
        {mode === "SEARCH" ? (
          <>
            <FormVanSearchField
              fullWidth
              label="Pick a saved list"
              value={selectedList}
              onChange={(_event, value) => setSelectedList(value)}
              campaignId={campaign.id}
              searchType={VanProxySearchType.SAVED_LIST}
            />
            or{" "}
            <Button
              variant="contained"
              size="small"
              onClick={() => setMode("LIST_ID")}
            >
              enter a list ID
            </Button>
          </>
        ) : (
          <>
            <FormTextField
              fullWidth
              variant="outlined"
              label="VAN saved list ID"
              value={enteredListId}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setEnteredListId(event.target.value)
              }
              onBlur={() => setEnteredListIdTouched(true)}
              helperText={
                enteredListIdTouched && !listId
                  ? "Must be a valid VAN list ID"
                  : null
              }
              style={{ marginBottom: 20 }}
              inputProps={{ style: { padding: "18.5px 14px" } }}
              InputProps={{
                classes: {
                  root: classes.listIdInputRoot,
                  input: formInputStyles.input,
                  notchedOutline: classes.hiddenNotchedOutline
                }
              }}
              InputLabelProps={{
                classes: {
                  root: `${formInputStyles.inputLabel} ${autocompleteStyles.inputLabel}`
                }
              }}
            />
            {!hideVanListSearch && (
              <>
                or{" "}
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setMode("SEARCH")}
                >
                  search for a list
                </Button>
              </>
            )}
          </>
        )}
      </div>

      <div>
        <SubmitButton
          disabled={!listId}
          isSubmitting={isSubmitting}
          submitText="Import"
          loadingText="Starting import..."
          onClick={handleSubmit}
        />
      </div>
    </>
  );
}
